import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_TRADE_PORTFOLIO, GET_TRADE_STOCK_LEVELS, GET_TRADING_GOAL_TYPES, GET_TRADING_GOALS, GET_USER_MANDATES,
} from '../queries/trading';
import {
  CONFIRM_ORDER, CREATE_TRADING_GOAL, EXECUTE_REBALANCE_TRADES, GENERATE_STOCK_TRADES, REBALANCE_TRADES_PREFLIGHT,
  UPDATE_TRADING_GOAL,
} from '../mutations/trading';
import { asClient } from '../clientScope';

/** Only an APPROVED mandate can be debited — everything else is informational. */
export const MANDATE_USABLE = 'APPROVED';

/**
 * The client's mandates, newest usable first.
 *
 * Read as the client, like every other client-scoped call, so an advisor sees
 * the mandates of whoever is on screen rather than their own.
 */
export function useNachMandates(clientId, { skip = false } = {}) {
  const off = skip || clientId == null || clientId === '';
  const { data, loading, error, refetch } = useQuery(GET_USER_MANDATES, {
    context: asClient(clientId),
    skip: off,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const rows = data?.getUserMandates ?? [];
    const usable = rows.filter((m) => String(m.mandateStatus).toUpperCase() === MANDATE_USABLE);
    return { rows, usable, loading, error, refetch };
  }, [data, loading, error, refetch]);
}

/* Read-only despite being a mutation: it persists nothing and sends no OTP. */
export const usePreflightTrades = () => useMutation(REBALANCE_TRADES_PREFLIGHT, { fetchPolicy: 'no-cache' });

/* Stages the basket and sends one OTP. Places nothing. */
export const useStageTrades = () => useMutation(EXECUTE_REBALANCE_TRADES, { fetchPolicy: 'no-cache' });

/* The call that actually places the staged basket. */
export const useConfirmOrder = () => useMutation(CONFIRM_ORDER, { fetchPolicy: 'no-cache' });

/**
 * The whole placement, as the screen experiences it: check → OTP → placed.
 *
 * Kept as one hook because the steps share state that must not drift — the
 * legs that were checked are the legs that get staged, and the order the OTP
 * belongs to is the order confirmed.
 */
export function useTradePlacement(clientId, legs, goalId) {
  const [preflight, preflightState] = usePreflightTrades();
  const [stage, stageState] = useStageTrades();
  const [confirm, confirmState] = useConfirmOrder();

  const [checked, setChecked] = useState(null);
  const [staged, setStaged] = useState(null);
  const [placed, setPlaced] = useState(null);
  const [failure, setFailure] = useState(null);

  const trades = useMemo(
    () => legs.map(({ securitySymbol, side, amount }) => ({
      securitySymbol, side, amount, allRedeem: false,
    })),
    [legs],
  );
  const vars = useMemo(
    () => ({ trades, goalId: goalId == null ? null : Number(goalId) }),
    [trades, goalId],
  );

  const check = useCallback(async () => {
    setFailure(null);
    try {
      const { data } = await preflight({ variables: vars, context: asClient(clientId) });
      setChecked(data?.rebalanceTradesPreflight ?? null);
    } catch (err) {
      setFailure(err.message);
    }
  }, [preflight, vars, clientId]);

  const sendOtp = useCallback(async ({ mandateId, paymentMode }) => {
    setFailure(null);
    try {
      const { data } = await stage({
        variables: {
          ...vars,
          mandateId: mandateId == null ? null : Number(mandateId),
          paymentMode,
        },
        context: asClient(clientId),
      });
      const r = data?.executeRebalanceTrades ?? null;
      if (r?.success) setStaged(r); else setFailure(r?.message || 'The basket was not staged.');
      return r;
    } catch (err) {
      setFailure(err.message);
      return null;
    }
  }, [stage, vars, clientId]);

  const place = useCallback(async (otp) => {
    setFailure(null);
    try {
      const { data } = await confirm({
        variables: { input: { orderId: Number(staged.orderId), otp: String(otp), sessionId: staged.sessionId } },
        context: asClient(clientId),
      });
      const r = data?.confirmOrder ?? null;
      if (r?.success) setPlaced(r); else setFailure(r?.message || 'The order was not placed.');
      return r;
    } catch (err) {
      setFailure(err.message);
      return null;
    }
  }, [confirm, staged, clientId]);

  const reset = useCallback(() => {
    setChecked(null); setStaged(null); setPlaced(null); setFailure(null);
  }, []);

  return {
    trades,
    checked,
    staged,
    placed,
    failure,
    checking: preflightState.loading,
    staging: stageState.loading,
    placing: confirmState.loading,
    check,
    sendOtp,
    place,
    reset,
    /* Which step the screen is on. */
    step: placed ? 'placed' : staged ? 'otp' : 'check',
  };
}

/**
 * A client's trading accounts of one type (`Trading`, `FnOTrading`,
 * `IntradayTrading`, `TradingBasket`), each with its mandate. Read as the
 * client, like `useNachMandates`, so an advisor sees the client's accounts.
 */
export function useTradingGoals(clientId, goalType = 'TRADING', { skip = false } = {}) {
  const off = skip || clientId == null || clientId === '';
  const { data, loading, error, refetch } = useQuery(GET_TRADING_GOALS, {
    variables: { goalType },
    context: asClient(clientId),
    skip: off,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return useMemo(() => ({
    rows: (data?.getTradingGoal ?? []).filter((g) => g?.userinvestmentgoalId),
    loading, error, refetch,
  }), [data, loading, error, refetch]);
}

/**
 * Trade ideas for stocks, sized for one of the client's trading accounts. The
 * account's mandate caps every size; each trade carries `risk_options`, the
 * lower / medium / higher risk sizes. Writes nothing.
 *
 * `generate(symbols, goalId, amountInr, objective)` resolves to the run, or
 * throws on a transport error; a refusal comes back as `success: false` with
 * its `message`. With `amountInr`, each trade also carries `for_amount`: that
 * sum, sized. `objective` is the desk's words (horizon, capital, risk) — the
 * same brief the agent ran with gives the same ideas.
 */
export function useStockTradeIdeas(clientId) {
  const [run, state] = useMutation(GENERATE_STOCK_TRADES, { fetchPolicy: 'no-cache' });
  const generate = useCallback(async (symbols, goalId, amountInr, objective) => {
    const { data } = await run({
      variables: {
        input: {
          symbols,
          goalId: goalId ? Number(goalId) : null,
          amountInr: amountInr ? Number(amountInr) : null,
          objective: objective || null,
        },
      },
      context: asClient(clientId),
    });
    return data?.generateStockTrades ?? null;
  }, [run, clientId]);
  return { generate, result: state.data?.generateStockTrades ?? null, loading: state.loading, error: state.error };
}

/**
 * A client's trading accounts as they stand (positions, P&L, value history,
 * payments and trades, limits in use). Read as the client, like the other
 * trading reads, and named explicitly so the backend checks the firm may act.
 */
export function useTradePortfolio(clientId, { skip = false } = {}) {
  const off = skip || clientId == null || clientId === '';
  const { data, loading, error, refetch } = useQuery(GET_TRADE_PORTFOLIO, {
    variables: { clientUserId: off ? null : Number(clientId) },
    context: asClient(clientId),
    skip: off,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return useMemo(() => ({
    portfolio: data?.getTradePortfolio ?? null,
    loading, error, refetch,
  }), [data, loading, error, refetch]);
}

/**
 * A stock's nearest supports and resistances, for drawing a held position
 * against them. Read on demand — the chart behind it takes a few seconds on a
 * cold stock — and kept in the cache, since the levels move once a day.
 */
export function useTradeStockLevels(symbol, { skip = false } = {}) {
  const { data, loading, error } = useQuery(GET_TRADE_STOCK_LEVELS, {
    variables: { symbol },
    skip: skip || !symbol,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return useMemo(() => ({ levels: data?.getTradeStockLevels ?? null, loading, error }), [data, loading, error]);
}

/**
 * Setting up a client's trading account, or changing its rules: the four
 * types with what each must state and its defaults (`types`, keyed by the
 * backend's goal type, e.g. `Trading`); `create(goalTypeEnum, mandate)` and
 * `update(goalTypeEnum, goalId, changes)` — fields in the backend's camelCase
 * names, only the changed ones on an update. Each resolves to the account, or
 * throws with the backend's reasons when the mandate does not hold.
 */
export function useTradingAccountSetup(clientId) {
  const typesQuery = useQuery(GET_TRADING_GOAL_TYPES, { fetchPolicy: 'cache-first', errorPolicy: 'all' });
  const [run, state] = useMutation(CREATE_TRADING_GOAL, { refetchQueries: [GET_TRADING_GOALS, GET_TRADE_PORTFOLIO] });
  const [runUpdate, updateState] = useMutation(UPDATE_TRADING_GOAL, { refetchQueries: [GET_TRADING_GOALS, GET_TRADE_PORTFOLIO] });
  const create = useCallback(async (goalTypeEnum, mandate) => {
    const { data } = await run({
      variables: { input: { goalType: goalTypeEnum, goal: {}, ...mandate } },
      context: asClient(clientId),
    });
    return data?.createTradingGoal ?? null;
  }, [run, clientId]);
  const update = useCallback(async (goalTypeEnum, goalId, changes) => {
    const { data } = await runUpdate({
      variables: { input: { goalType: goalTypeEnum, userinvestmentgoalId: Number(goalId), ...changes } },
      context: asClient(clientId),
    });
    return data?.updateTradingGoal ?? null;
  }, [runUpdate, clientId]);
  const types = useMemo(() => Object.fromEntries((typesQuery.data?.tradingGoalTypes ?? []).map((t) => [t.goalType, t])), [typesQuery.data]);
  return {
    types, typesLoading: typesQuery.loading, create, update,
    saving: state.loading || updateState.loading, error: state.error || updateState.error,
  };
}
